/**
 * Ask Gemini suggestions: dashboard-based initially, conversation-based after each reply.
 */
import { getInsightSuggestions } from './dashboardInsightAi';

const FALLBACK_SUGGESTIONS = [
  {
    label: 'Executive summary for this period',
    prompt:
      'Give an executive summary of my dashboard for the selected time range: highlight what is healthy, what is at risk, and the top 3 actions I should take. Use only the snapshot data and note any gaps.',
  },
  {
    label: 'What needs attention first?',
    prompt:
      'Based on the dashboard snapshot, prioritize what needs my attention first (inventory, credit, sales trend, expenses). Explain why each item matters and suggest concrete next steps.',
  },
  {
    label: 'Sales vs expenses health check',
    prompt:
      'Analyze how sales, expenses, and profit relate in this period. Is the business financially healthy? Call out anomalies and what to investigate.',
  },
  {
    label: 'Check data reliability',
    prompt:
      'Review the dashboard snapshot and tell me which metrics look incomplete, zero, or inconsistent, and how that limits your analysis.',
  },
];

const TOPIC_FOLLOW_UPS = {
  concentration: [
    {
      label: 'How to reduce concentration risk?',
      prompt:
        'Based on your last answer and the dashboard snapshot, how can we reduce revenue concentration on a few products? Give practical steps for a retail/POS business.',
    },
    {
      label: 'Impact if top seller drops',
      prompt:
        'If our top-selling product underperforms next month, what is the likely revenue impact given the snapshot? Suggest mitigation actions.',
    },
    {
      label: 'Under-served categories to grow',
      prompt:
        'From the dashboard data, which product categories or segments look under-represented vs our best sellers, and how could we grow them?',
    },
  ],
  credit: [
    {
      label: 'Collection priority this week',
      prompt:
        'Based on your previous analysis and credit data in the snapshot, who should we call first and what collection script or terms should we use?',
    },
    {
      label: 'Credit policy adjustments',
      prompt:
        'Should we tighten credit limits or payment terms? Recommend policy changes grounded in the snapshot and your last answer.',
    },
    {
      label: 'Bad debt risk estimate',
      prompt:
        'How much of our outstanding credit looks at risk of not being collected? Explain your reasoning from the dashboard data.',
    },
  ],
  inventory: [
    {
      label: 'Restock priority order',
      prompt:
        'Give a prioritized restock list from the snapshot low/out-of-stock items, with reasoning on revenue impact and urgency.',
    },
    {
      label: 'Stock-out business impact',
      prompt:
        'Quantify or estimate the business impact of current out-of-stock items using the dashboard snapshot. What sales are we likely losing?',
    },
    {
      label: 'Safety stock recommendations',
      prompt:
        'For our highest-risk SKUs in the snapshot, what minimum stock levels would you recommend and why?',
    },
  ],
  profit: [
    {
      label: 'Top levers to improve margin',
      prompt:
        'From your last answer and the snapshot (sales, expenses, profit), what are the top 3 levers to improve margin this month?',
    },
    {
      label: 'Expense categories to cut',
      prompt:
        'Which expense areas in the snapshot look disproportionate, and what cuts or controls are realistic without hurting operations?',
    },
    {
      label: 'Is this profit level sustainable?',
      prompt:
        'Is the current profit trend sustainable for the next quarter? Use the snapshot chart and totals in your reasoning.',
    },
  ],
  sales: [
    {
      label: 'Investigate the sales spike',
      prompt:
        'You mentioned sales movement in your last answer. Help me verify what drove it using the snapshot chart and metrics, and whether it is an anomaly.',
    },
    {
      label: 'Sustain recent sales momentum',
      prompt:
        'If recent sales are strong, what operational actions keep momentum going? Tie recommendations to the dashboard snapshot.',
    },
    {
      label: 'Why did sales soften?',
      prompt:
        'If sales weakened in the snapshot trend, what are the most likely causes and what should we check in store operations?',
    },
  ],
  executive: [
    {
      label: 'Top 3 actions for this week',
      prompt:
        'Convert your last summary into a concrete 7-day action plan with owners (sales, inventory, credit, finance) and expected outcomes.',
    },
    {
      label: 'Biggest risk you flagged',
      prompt:
        'Expand on the single biggest risk you mentioned: root cause, early warning signs, and how we measure improvement.',
    },
    {
      label: 'What is going well?',
      prompt:
        'From the snapshot and your prior answer, what is genuinely healthy in the business and how do we protect those strengths?',
    },
  ],
  attention: [
    {
      label: 'Deep dive on #1 priority',
      prompt:
        'Take the first priority you listed and give a step-by-step playbook to address it using only dashboard snapshot data.',
    },
    {
      label: 'Quick wins vs long-term fixes',
      prompt:
        'Separate your recommendations into quick wins (this week) vs structural fixes (this quarter) based on the snapshot.',
    },
    {
      label: 'Metrics to track weekly',
      prompt:
        'Which 5 KPIs from the dashboard should we review every week to know if we are improving?',
    },
  ],
  data: [
    {
      label: 'What data should we capture next?',
      prompt:
        'What additional data (not in the snapshot) would make your analysis more reliable? Suggest practical fields for a POS business.',
    },
    {
      label: 'Re-run analysis assumptions',
      prompt:
        'List assumptions you made in your last answer that could be wrong if snapshot data is incomplete, and how to validate them.',
    },
  ],
  trial: [
    {
      label: 'Pre-renewal checklist',
      prompt:
        'Build a checklist before trial/subscription renewal based on snapshot performance and your last recommendations.',
    },
  ],
};

const pushUnique = (list, item, max, usedLabels = new Set()) => {
  if (list.length >= max) return;
  const key = item.label.toLowerCase();
  if (usedLabels.has(key)) return;
  if (list.some((s) => s.label.toLowerCase() === key)) return;
  usedLabels.add(key);
  list.push(item);
};

const normalize = (text) => (text || '').toLowerCase();

/** Detect themes from user question + assistant reply */
const detectTopics = (userText, assistantText) => {
  const t = `${normalize(userText)} ${normalize(assistantText)}`;
  const topics = [];

  const add = (topic, pattern) => {
    if (pattern.test(t)) topics.push(topic);
  };

  add('concentration', /concentrat|best.?sell|top product|few products|dependency|revenue mix/);
  add('credit', /credit|due|collection|receivable|overdue|outstanding balance|pending payment/);
  add('inventory', /inventory|stock|replenish|out of stock|low stock|sku|restock/);
  add('profit', /profit|margin|expense|cost|spending|financial health|loss/);
  add('sales', /sales|revenue|trend|surge|spike|momentum|bills|chart|may|month|growth/);
  add('executive', /executive|summary|overview|holistic|in summary|overall/);
  add('attention', /attention|priority|first|urgent|focus|needs? to/);
  add('data', /data|reliab|incomplete|gap|missing|limit|placeholder|snapshot/);
  add('trial', /trial|subscription|renew|plan end/);

  return [...new Set(topics)];
};

/** Extra one-off follow-ups parsed from assistant wording */
const getAssistantTriggeredFollowUps = (assistantText) => {
  const t = normalize(assistantText);
  const items = [];

  if (/surge|spike|jump|sharp increase/.test(t)) {
    items.push({
      label: 'Validate the sales surge',
      prompt:
        'You mentioned a sales surge. Cross-check it against the snapshot chart and totals—is it real, which period, and what likely caused it?',
    });
  }
  if (/monitor.*inventory|inventory.*monitor|restock/.test(t)) {
    items.push({
      label: 'Inventory actions from your advice',
      prompt:
        'Based on your last reply about inventory, list exact restock and monitoring actions for this week using snapshot SKUs.',
    });
  }
  if (/credit|collection|due/.test(t)) {
    items.push({
      label: 'Credit follow-up plan',
      prompt:
        'Turn your credit-related advice into a 7-day follow-up plan with customer priority order from the snapshot.',
    });
  }
  if (/expense|cost/.test(t)) {
    items.push({
      label: 'Expense control next steps',
      prompt:
        'Expand on expense concerns from your last answer: what to audit first and expected savings ranges from snapshot figures.',
    });
  }
  if (/investigate|check|verify/.test(t)) {
    items.push({
      label: 'Investigation checklist',
      prompt:
        'Create a short investigation checklist for the issue you told me to investigate, mapped to fields in the dashboard snapshot.',
    });
  }

  return items;
};

/** Skip suggestions too similar to what the user just asked */
const isSimilarToLastQuestion = (label, lastUserMessage) => {
  const u = normalize(lastUserMessage);
  const words = normalize(label).split(/\s+/).filter((w) => w.length > 3);
  const matches = words.filter((w) => u.includes(w));
  return matches.length >= 2;
};

/**
 * Follow-ups after user has sent at least one message.
 */
export const getConversationFollowUpSuggestions = (
  { lastUserMessage, lastAssistantMessage, snapshot },
  max = 4
) => {
  const out = [];
  const usedLabels = new Set();

  const topics = detectTopics(lastUserMessage, lastAssistantMessage);

  // User-question topics first (order preserved), then assistant-only triggers
  for (const topic of topics) {
    const pool = TOPIC_FOLLOW_UPS[topic] || [];
    for (const item of pool) {
      if (isSimilarToLastQuestion(item.label, lastUserMessage)) continue;
      pushUnique(out, item, max, usedLabels);
      if (out.length >= max) break;
    }
    if (out.length >= max) return out;
  }

  for (const item of getAssistantTriggeredFollowUps(lastAssistantMessage)) {
    if (isSimilarToLastQuestion(item.label, lastUserMessage)) continue;
    pushUnique(out, item, max, usedLabels);
    if (out.length >= max) break;
  }

  // Secondary pools not yet used — pick one from each remaining topic
  const allTopics = Object.keys(TOPIC_FOLLOW_UPS);
  for (const topic of allTopics) {
    if (topics.includes(topic)) continue;
    const pool = TOPIC_FOLLOW_UPS[topic];
    const pick = pool.find(
      (item) => !isSimilarToLastQuestion(item.label, lastUserMessage)
    );
    if (pick) pushUnique(out, pick, max, usedLabels);
    if (out.length >= max) break;
  }

  const periodLabel = snapshot?.timeRange?.label || 'this period';
  const contextualFill = [
    {
      label: 'Next steps for this week',
      prompt: `Based on your last answer about ${periodLabel}, what should we do in the next 7 days? Be specific and tie each step to snapshot metrics.`,
    },
    {
      label: 'Challenge your assumptions',
      prompt:
        'What assumptions in your last answer might be wrong given the snapshot? How would the recommendation change?',
    },
    {
      label: 'Compare to prior period',
      prompt:
        'Using the snapshot time range and chart, how does this period compare to earlier periods and what changed operationally?',
    },
  ];

  for (const item of contextualFill) {
    pushUnique(out, item, max, usedLabels);
    if (out.length >= max) break;
  }

  return out.slice(0, max);
};

/**
 * @param {object|null} snapshot from buildDashboardAiSnapshot
 * @returns {{ label: string, prompt: string }[]}
 */
export const getDashboardAiSuggestions = (snapshot, max = 4) => {
  if (!snapshot) {
    return FALLBACK_SUGGESTIONS.slice(0, max);
  }

  const out = [];
  const overview = snapshot.overview || {};
  const alerts = snapshot.alerts || {};
  const inventory = snapshot.inventory || {};
  const sales = snapshot.salesOverview?.metrics || {};
  const periodLabel = snapshot.timeRange?.label || 'this period';

  const creditDue = Number(alerts.creditDueAmount ?? overview.totalCreditDue ?? 0);
  const creditCustomers = Number(alerts.creditCustomerCount ?? overview.totalCreditCustomers ?? 0);
  const lowStock = Number(inventory.lowStock ?? overview.lowStockCount ?? 0);
  const outOfStock = Number(inventory.outOfStock ?? overview.outOfStockCount ?? 0);
  const salesAmount = Number(overview.totalSalesAmount ?? 0);
  const expenses = Number(overview.totalExpenses ?? 0);
  const profit = Number(overview.totalProfit ?? 0);

  if (creditDue > 0 || creditCustomers > 0) {
    pushUnique(out, {
      label: 'Explain credit exposure & risk',
      prompt: `Analyze credit due (₹${creditDue}) and pending customer balances in the snapshot for ${periodLabel}. Which accounts look highest risk, and what collection or policy actions do you recommend?`,
    }, max);
  }

  if (outOfStock > 0 || lowStock > 0) {
    pushUnique(out, {
      label: 'Summarize inventory risks',
      prompt:
        'Review low-stock and out-of-stock items in the snapshot. Which products are most critical to restock, what revenue or fulfillment risk do they create, and what should we prioritize?',
    }, max);
  }

  if (salesAmount > 0 && expenses > 0) {
    const margin = profit / salesAmount;
    if (profit < 0 || margin < 0.12) {
      pushUnique(out, {
        label: 'Why is profit under pressure?',
        prompt: `For ${periodLabel}, sales, expenses, and profit are in the snapshot. Explain what is compressing profit margin and suggest practical levers (cost control, pricing, mix) grounded in the data.`,
      }, max);
    }
  }

  if (expenses > 0 && salesAmount > 0 && expenses / salesAmount > 0.85) {
    pushUnique(out, {
      label: 'Are expenses too high vs sales?',
      prompt:
        'Compare total expenses to sales in the dashboard snapshot. Is spending proportionate? Highlight drivers and whether this pattern is sustainable.',
    }, max);
  }

  const trialDays = alerts.trialDaysRemaining;
  if (trialDays != null && trialDays <= 14) {
    pushUnique(out, {
      label: 'Trial ending — focus areas',
      prompt: `We have about ${trialDays} day(s) left on trial. Using dashboard metrics, what should we fix or prove before renewal?`,
    }, max);
  }

  const subDays = alerts.subscriptionDaysRemaining;
  if (subDays != null && subDays <= 14) {
    pushUnique(out, {
      label: 'Subscription renewal priorities',
      prompt: `Subscription renews in about ${subDays} day(s). Based on dashboard performance, what business outcomes should we improve before renewal?`,
    }, max);
  }

  const pending = snapshot.pendingPayments || [];
  if (pending.length > 0) {
    pushUnique(out, {
      label: 'Prioritize overdue collections',
      prompt:
        'Using pending payment / credit customer data in the snapshot, who should we follow up with first and why? Suggest a collection priority order.',
    }, max);
  }

  const chartData = snapshot.salesOverview?.chartData || [];
  if (chartData.length >= 3) {
    const nums = chartData.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
    if (nums.length >= 3) {
      const recent = nums.slice(-3);
      const earlier = nums.slice(0, 3);
      const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
      const earlierAvg = earlier.reduce((a, b) => a + b, 0) / earlier.length;
      if (earlierAvg > 0 && recentAvg < earlierAvg * 0.85) {
        pushUnique(out, {
          label: 'Why is sales trend weakening?',
          prompt:
            'The sales chart in the snapshot shows a recent slowdown. Explain possible causes visible in the data and what I should investigate operationally.',
        }, max);
      } else if (earlierAvg > 0 && recentAvg > earlierAvg * 1.15) {
        pushUnique(out, {
          label: 'What is driving sales momentum?',
          prompt:
            'Sales trend in the snapshot looks positive recently. What likely drivers can you infer from the dashboard data, and how do we sustain it?',
        }, max);
      }
    }
  }

  if (sales.growthPercent != null && Number(sales.growthPercent) < -5) {
    pushUnique(out, {
      label: 'Diagnose declining sales growth',
      prompt: `Sales growth is negative (${sales.growthPercent}%) in the snapshot metrics. Help diagnose likely reasons using all available dashboard fields.`,
    }, max);
  }

  const best = snapshot.bestSellers || [];
  if (best.length >= 2) {
    pushUnique(out, {
      label: 'Revenue concentration risk',
      prompt:
        'Using best-seller data in the snapshot, is revenue too concentrated on a few products? Explain risks and diversification ideas.',
    }, max);
  }

  for (const fallback of FALLBACK_SUGGESTIONS) {
    pushUnique(out, fallback, max);
  }

  return out.slice(0, max);
};

const mergeSuggestions = (primary, secondary, max) => {
  const out = [];
  const used = new Set();
  const add = (item) => {
    if (out.length >= max || !item?.label) return;
    const key = item.label.toLowerCase();
    if (used.has(key)) return;
    used.add(key);
    out.push(item);
  };
  (primary || []).forEach(add);
  (secondary || []).forEach(add);
  return out.slice(0, max);
};

/**
 * Initial = dashboard or insight-specific; after Q&A = follow-ups (biased by insight if opened from a card).
 */
export const getAiChatSuggestions = ({ snapshot, messages, insightContext }, max = 4) => {
  const list = Array.isArray(messages) ? messages : [];
  const userMessages = list.filter((m) => m.role === 'user');

  if (userMessages.length === 0) {
    if (insightContext) {
      return getInsightSuggestions(insightContext, snapshot, max);
    }
    return getDashboardAiSuggestions(snapshot, max);
  }

  const lastUser = userMessages[userMessages.length - 1]?.content || '';
  let lastAssistant = '';
  const lastIdx = list.length - 1;
  if (lastIdx >= 0 && list[lastIdx].role === 'assistant') {
    lastAssistant = list[lastIdx].content || '';
  } else {
    const assistants = list.filter((m) => m.role === 'assistant');
    lastAssistant = assistants[assistants.length - 1]?.content || '';
  }

  const followUps = getConversationFollowUpSuggestions(
    { lastUserMessage: lastUser, lastAssistantMessage: lastAssistant, snapshot },
    max
  );

  if (insightContext) {
    const insightPool = getInsightSuggestions(insightContext, snapshot, max);
    if (!lastAssistant) {
      return mergeSuggestions(insightPool, followUps, max);
    }
    return mergeSuggestions(followUps, insightPool, max);
  }

  return followUps;
};

export default getAiChatSuggestions;
